import React, { useState, useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

// Services
import LocalStorageService from '../../services/LocalStorageService'
import NotificationService from '../../services/NotificationService'
import OrganisationService from '../../services/OrganisationService'
import PasswordService from '../../services/PasswordService'
import AdminService from '../../services/AdminService'

// Appt Components
import Anchor from '../../components/simple/anchor/Anchor'
import Box from '../../components/simple/box/Box'
import Button from '../../components/simple/button/Button'
import Form from '../../components/simple/form/Form.js'
import FormField from '../../components/simple/formField/FormField.js'
import ResponsiveGrid from '../../components/compound/responsiveGrid/ResponsiveGrid'
import OptionButtons from '../../components/compound/optionButtons/OptionButtons'
import Text from '../../components/simple/text/Text'
import TextArea from '../../components/simple/input/TextArea'
import TextInput from '../../components/simple/input/TextInput'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PulseLoader from 'react-spinners/PulseLoader'
import { ResponsiveContext } from 'grommet'
import DataTable from '../../components/simple/dataTable/DataTable.js'
import Moment from 'react-moment'
import Divider from '../../components/simple/divider/Divider.js'
import H3 from '../../components/simple/heading/H3.js'


function OrganisationEdit (props) {
  const navigate = useNavigate()

  const [organisationDetails, setOrganisationDetails] = useState({
    name: '',
    addressLineOne: '',
    postcode: '',
    active: '',
    city: '',
    notes: '',
    admin: '',
    label: '',
    wondeSync: '',
    wondeLastStatus: '',
    cpomsEnabled: '',
    cpomsDomain: '',
    plan: '',
    cpomsLowLevelCategoryId: '',
    cpomsHighLevelCategoryId: '',
    existingOrgAdmin: false,
    hasSites: false,
    numAllowedUsers: undefined,
    numUsers: 0
  })

  const [userCount, setUserCount] = useState(0)

  const [userValues, setUserValues] = useState([])
  const [studentValues, setStudentValues] = useState([])
  const [showDeleteOptions, showDelete] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // const { error, showError } = useErrorHandler(null)

  const params = useParams()

  const organisationId = params.organisationid

  const apiToken = LocalStorageService.get('apiToken')

  // Submit form values
  const submitForm = async values => {
    // if there wasn't an orgadmin when we started this, submit the new org admin
    const orgData = {
      name: values.name,
      addressLineOne: values.addressLineOne,
      postcode: values.postcode,
      active: values.active === 'Yes',
      cpomsEnabled: values.cpomsEnabled === 'Yes',
      cpomsDomain: values?.cpomsDomain?.split('.cpoms.net').join('').trim(),
      wondeSync: values.wondeSync === 'Yes',
      wondeLastStatus: values.wondeLastStatus || null,
      cpomsHighLevelCategoryId: values.cpomsHighLevelCategoryId || null,
      cpomsLowLevelCategoryId: values.cpomsLowLevelCategoryId || null,
      city: values.city,
      numAllowedUsers: values.numAllowedUsers,
      notes: values.notes,
      label: values.label,
      plan: values.plan,
      hasSites: !!values.hasSites,
    };


    const cpomsTokens = values?.cpomsToken?.trim()

    if (organisationId === 'new') {
      const organisationAdded = await OrganisationService.addOrganisation(
        apiToken,
        orgData,
      );
      if (organisationAdded.error) {
        NotificationService.error(
          'Error adding the organisation, please try again - ' +
            organisationAdded.error,
        );
      } else {
        NotificationService.info('Organisation added');

        if (values.orgAdmin) {
          const userAdded = await OrganisationService.setOrganisationAdmin(
            apiToken,
            organisationAdded?.data?.[0]?.id,
            {
              email: values.orgAdmin,
            },
          );
          if (userAdded.error) {
            NotificationService.error(
              'Error adding the Organisation Admin, please try again',
            );
          }
          // reload the screen after setting an orgadmin
          window.location.reload();
        }

        if (organisationAdded?.data?.[0]?.id) {
          navigate(`/service/${organisationAdded?.data?.[0]?.id}/edit`);
        }
      }
    } else {
      const organisationUpdated = await OrganisationService.updateOrganisation(
        apiToken,
        orgData,
        organisationId,
      );
      if (organisationUpdated.error) {
        NotificationService.error(
          'Error updating the organisation, please try again - ' +
            organisationUpdated.error,
        );
      } else {
        NotificationService.info('Organisation updated');
      }

      if (cpomsTokens) {
        const tokenAdded = await OrganisationService.addCpomsToken(
          apiToken,
          organisationId,
          cpomsTokens,
        )
      }

      if (values.orgAdmin) {
        const userAdded = await OrganisationService.setOrganisationAdmin(
          apiToken,
          organisationId,
          {
            email: values.orgAdmin,
          },
        );
        if (userAdded.error) {
          NotificationService.error(
            'Error adding the Organisation Admin, please try again',
          );
        }
        // reload the screen after setting an orgadmin
        window.location.reload();
      }
    }
  };

  const goBack = () => {
    navigate(`/services`)
  }

  const doWondeSync = async (organisationId) => {
    if (!organisationId) {
      NotificationService.error('No organisationId supplied')
      return
    }

    try {
      await AdminService.doWondeSchoolSync(apiToken, organisationId)
    } catch (err) {
      NotificationService.error('Unable to start sync - please try again', err)
      return
    }

    NotificationService.success('Wonde Sync has been started - this may take up to 10 minutes to complete')
  }

  const doCpomsSync = async (organisationId) => {
    if (!organisationId) {
      NotificationService.error('No organisationId supplied')
      return
    }
    
    try {
      await AdminService.doCpomsSchoolSync(apiToken, organisationId)
    } catch (err) {
      NotificationService.error('Cpoms Sync error - ', err)
      return
    }

    NotificationService.success('Cpoms Sync has been started - this may take up to 10 minutes to complete')
  }

  useEffect(() => {
    let unmounted = false

    // Get organisation details
    const getOrganisationDetails = async (organisationId) => {


      const params = {
        fields: 'id,name,addressLineOne,admin,postcode,active,notes,city,numAllowedUsers,plan,label,cpomsKey,cpomsEnabled,cpomsDomain,cpomsSyncEverything,hasSites,wondeSync,wondeLastStatus,cpomsLowLevelCategoryId,cpomsHighLevelCategoryId',
        limit: 1
      }
      const organisationDetails = await OrganisationService.getOrganisation(apiToken, params, organisationId)

      if (organisationDetails?.error) {
        NotificationService.error(organisationDetails.error)
      } else {
        if (!unmounted) {
          const data = organisationDetails.data[0]
          data.hasSites = !!data.hasSites

          data.active = data.active ? 'Yes' : 'No'
          data.wondeSync = data.wondeSync ? 'Yes' : 'No'

          data.cpomsEnabled = data.cpomsEnabled ? 'Yes' : 'No'
          if (data.admin) {
            data.existingOrgAdmin = true
          }

          delete data.admin

          setOrganisationDetails(data)
        }
      }
    }

    if (organisationId !== 'new') {
      getOrganisationDetails(organisationId)
    }

    return () => { unmounted = true }
  }, [])


  const [sort, setSort] = useState({
    property: 'name',
    direction: 'asc'
  })


  // Reset User password
  const resetPassword = async (email) => {

    if (!email) {
      NotificationService.error('No email supplied')
      return
    }

    if (!organisationId) {
      NotificationService.error('No organisationId supplied')
      return
    }

    try {
      var passwordResetStatus = await PasswordService.resetPasswordRequest(organisationId, email)
    } catch (err) {
      NotificationService.error('Password reset error - ', err)
      return
    }

    NotificationService.success('Password reset email has been sent')
  }

  const getUsers = async () => {
    const apiToken = LocalStorageService.get('apiToken')

    if (!organisationId) return false

    var params = {
      fields: 'id,firstName,lastName,email,userName,reference,ethnicity,gender,createdAt',
      limit: 1000,
    }

    params.where = {
      type: 'user'
    }

    setLoading(true)
    const users = await OrganisationService.getUsers(apiToken, params, organisationId)
    if (users?.data) {
      const mappedUsers = users.data.map((data, index) => ({
        id: data.id,
        name: data.firstName + ' ' + data.lastName,
        email: data.email,
        created: <Moment format='MMM Do YYYY-HH:mm'>{data.createdAt}</Moment>
      }))
      setUserValues(mappedUsers)
    }
    setLoading(false)
  }

  const getStudents = async () => {
    const apiToken = LocalStorageService.get('apiToken')

    if (!organisationId) return false

    var params = {
      fields: 'id,firstName,lastName,email,reference,createdAt',
      limit: 1000,
    }

    params.where = {
      type: 'student'
    }

    setLoading(true)
    const students = await OrganisationService.getStudents(apiToken, params, organisationId)
    if (students?.data) {
      const mappedStudents = students.data.map((data, index) => ({
        id: data.id,
        name: data.firstName + data.lastName,
        reference: data.reference,
        created: <Moment format='MMM Do YYYY-HH:mm'>{data.createdAt}</Moment>
      }))
      setStudentValues(mappedStudents)
    }
    setLoading(false)
  }


  // Basically componentDidMount
  useEffect(() => {
    getUsers()
    getStudents()
  }, [organisationId])


  const userColumns = [
    {
      property: 'name',
      header: <Text weight={500}>Name</Text>
    },
    {
      property: 'email',
      header: <Text weight={500}>Email</Text>
    },
    {
      property: 'created',
      header: <Text weight={500}>Created</Text>
    },
    {
      property: 'actions',
      header: <Text weight={500}>Actions</Text>,
      render: datum => (
        <Box direction='row'>
          <Button
            onClick={() => resetPassword(datum.email)}
            target='_self'
            plain
            children={
              <Box pad='5px'>
                <FontAwesomeIcon icon={['fal', 'key']} color='#fe8300' />

              </Box>
            }
          />
        </Box>
      )
    }
  ]

  const studentColumns = [
    {
      property: 'name',
      header: <Text weight={500}>Name</Text>
    },
    {
      property: 'reference',
      header: <Text weight={500}>Reference</Text>
    },
    {
      property: 'created',
      header: <Text weight={500}>Created</Text>
    }
  ]

  return (
    <Box width="xlarge">
      <Box
        background="white"
        direction="column"
        gap="small"
        margin={{ bottom: 'medium' }}
        round="small"
      >
        <Box direction="column" pad={{ horizontal: 'medium' }}>
          <Text margin={{ top: 'small' }} size="xlarge">
            System Organisations:{' '}
            {organisationDetails.id
              ? organisationDetails.name
              : 'New Organisation'}
          </Text>
          <Text margin={{ bottom: 'small', top: 'xsmall' }} size="xsmall">
            <Anchor href="/">Behaviour Smart</Anchor>
            <Text color="brand" size="xsmall">
              {location.pathname}
            </Text>
          </Text>
        </Box>
      </Box>

       <Box background='white' direction='column' gap='small' round='small' pad='small' margin={{ bottom: 'medium' }}>
        <Box direction='row' gap='small' pad={{ horizontal: 'xsmall', vertical: 'xsmall' }}>
          <H3 margin="none">Actions:</H3>
          <Button label='Wonde Sync' primary onClick={() => doWondeSync(organisationId)} disabled={hasUnsavedChanges || organisationDetails?.wondeSync !== 'Yes'} />
          <Button label='Cpoms Sync' primary onClick={() => doCpomsSync(organisationId)} disabled={hasUnsavedChanges || organisationDetails?.cpomsEnabled !== 'Yes'} />
        </Box>
      </Box>

      <Box background="white" direction="column" gap="small" round="small">
        <Box
          gap="small"
          margin={{ horizontal: 'small' }}
          pad="small"
          round="small"
        >
          <Box direction="column" gap="medium">
            <Form
              //   validate='blur'
              onChange={nextValue => {
                // console.log(nextValue)
                setHasUnsavedChanges(true)
                setOrganisationDetails(nextValue);
              }}
              onSubmit={({ value: nextValue }) => {
                // console.log(nextValue)
                setHasUnsavedChanges(false)
                submitForm(nextValue);
              }}
              value={organisationDetails}
            >
              <ResponsiveGrid
                rows={['auto']}
                columns={{
                  small: ['auto'],
                  medium: ['auto', 'auto'],
                  large: ['auto', 'auto'],
                  xlarge: ['auto', 'auto'],
                }}
                gap="small"
              >
                <Box direction="column">
                  <Text>Name</Text>

                  <FormField
                    direction="column"
                    label="Organisation Name"
                    name="name"
                    required
                  >
                    <TextInput name="name" type="text" />
                  </FormField>

                  <Box direction="column">
                    <Text>Address</Text>

                    <Box direction="row-responsive" gap="medium">
                      <FormField
                        direction="column"
                        label="Address"
                        name="addressLineOne"
                        width="100%"
                      >
                        <TextInput name="addressLineOne" type="text" />
                      </FormField>

                      <FormField
                        direction="column"
                        label="Postcode"
                        name="postcode"
                      >
                        <TextInput name="postcode" type="text" />
                      </FormField>

                      <FormField direction="column" label="City" name="city">
                        <TextInput name="city" type="text" />
                      </FormField>
                    </Box>
                  </Box>

                  <FormField
                    direction="column"
                    label="Other Notes"
                    name="notes"
                  >
                    <TextArea name="notes" type="text" />
                  </FormField>

                  {organisationId !== 'new' && <>

                  <Divider margin={{ top: 'medium', bottom: 'medium' }} />

                   <Text>Organisation Settings</Text>

                    <ResponsiveGrid
                rows={['auto']}
                columns={{
                  small: ['auto'],
                  medium: ['auto', 'auto', 'auto'],
                  large: ['auto', 'auto', 'auto'],
                  xlarge: ['auto', 'auto', 'auto'],
                }}
                gap="small"
              >
                  {organisationId !== 'new'  && (
                    <FormField
                      direction="column"
                      label="Organisation Admin"
                      name="orgAdmin"
                      width="small"
                    >

                      {organisationDetails.existingOrgAdmin && <Text color="red">Adding a new admin will replace the old one.</Text>}
                      {organisationDetails?.active !== 'Yes' && <Text color="red">Must be active to set admin</Text>}

                      {organisationDetails?.active == 'Yes' && <TextInput
                        name="orgAdmin"
                        type="text"
                      />}
                    </FormField>
                  )}

                  <Box direction="row-responsive" gap="medium">
                    {/* <FormField
                      direction='column'
                      label='# Users'
                    >
                      <Text size='large'>{userCount}</Text>
                    </FormField> */}

                    <Box direction="column">
                      <FormField
                        direction="column"
                        label="User Limit"
                        name="numAllowedUsers"
                      >
                        <TextInput name="numAllowedUsers" type="text" />
                      </FormField>

                      <Text size="xsmall">Leave blank for no limit</Text>
                    </Box>
                  </Box>
                  <Box direction="row-responsive" gap="medium">
                    <FormField direction="column" label="Active" name="active">
                      <OptionButtons
                        id="active"
                        name="active"
                        // value='Yes'
                      />
                    </FormField>
                  </Box>

                  <Box direction="row-responsive" gap="medium">
                    <FormField direction="column" label="Plan" name="plan">
                      <OptionButtons
                        id="plan"
                        name="plan"
                        options={['Lite', 'Plus', 'Care']}
                        // value='Yes'
                      />
                    </FormField>
                  </Box>

                  <Box direction="row-responsive" gap="medium">
                    <FormField direction="column" label="Label" name="label">
                      <OptionButtons
                        id="label"
                        name="label"
                        options={['Green', 'Yellow', 'Red']}
                      />
                    </FormField>
                  </Box>

                </ResponsiveGrid>
                </>}
                {organisationId !== 'new' && <>
                                    <Divider margin={{ top: 'medium', bottom: 'medium' }} />


                  <Text>Wonde Settings</Text>

                    <ResponsiveGrid
                rows={['auto']}
                columns={{
                  small: ['auto'],
                  medium: ['auto', 'auto', 'auto'],
                  large: ['auto', 'auto', 'auto'],
                  xlarge: ['auto', 'auto', 'auto'],
                }}
                gap="small"
              >

                    <FormField direction="column" label="Wonde Enabled" name="wondeSync">
                      <OptionButtons
                        id="wondeSync"
                        name="wondeSync"
                        options={['Yes', 'No']}
                      />
                    </FormField>
                    <FormField direction="column" label="Wonde Last Status" name="wondeLastStatus">
                      <Text>{organisationDetails?.wondeLastStatus || 'n/a'}</Text>
                    </FormField>


              </ResponsiveGrid>


              <Divider margin={{ top: 'medium', bottom: 'medium' }} />
               <Text>Cpoms Settings</Text>

                    <ResponsiveGrid
                rows={['auto']}
                columns={{
                  small: ['auto'],
                  medium: ['auto', 'auto', 'auto'],
                  large: ['auto', 'auto', 'auto'],
                  xlarge: ['auto', 'auto', 'auto'],
                }}
                gap="small"
              >


                    <FormField direction="column" label="Cpoms Enabled" name="cpomsEnabled">
                      <OptionButtons
                        id="cpomsEnabled"
                        name="cpomsEnabled"
                        options={['Yes', 'No']}
                      />
                    </FormField>
                    {/* Cpoms Domain */}
                    <FormField direction="column" label="Cpoms Domain" name="cpomsDomain">
                      <TextInput name="cpomsDomain" type="text" />
                    </FormField>

                    {/* CPOMs low level category */}
                    <FormField direction="column" label="Cpoms Low Level Category" name="cpomsLowLevelCategoryId">
                      <TextInput name="cpomsLowLevelCategoryId" type="text" />
                    </FormField>

                    {/* CPOMs high level category */}
                    <FormField direction="column" label="Cpoms High Level Category" name="cpomsHighLevelCategoryId">
                      <TextInput name="cpomsHighLevelCategoryId" type="text" />
                    </FormField>

                    {/* CPOMS token, only populated if changing the token */}
                    <FormField direction="column" label="Cpoms Token (only populate if changing)" name="cpomsToken">
                      <TextInput name="cpomsToken" type="text" />
                    </FormField>


                  </ResponsiveGrid>
                  </>}

                </Box>

              </ResponsiveGrid>

              <Box direction="row" justify="between" margin={{ top: 'medium' }}>
                <Button label="< Back" onClick={() => goBack()} secondary />
                <Button type="submit" label="Save" primary />
              </Box>
            </Form>
          </Box>
        </Box>
      </Box>
     

      <Box background='white' direction='column' gap='small' round='small' margin={{ top: 'medium', bottom: 'large' }}>

        <Box direction='row-responsive' gap='xlarge' justify='between' pad={{ horizontal: 'medium', top: 'medium' }}>
          <Box>
            <Text color='#000' size='large' weight='bold'>System Users</Text>
          </Box>
          <Box direction='row' gap='xsmall'>
          </Box>
        </Box>

        <Box direction='column' gap='medium' pad={{ horizontal: 'medium', bottom: 'medium' }}>
          <Box align='center'>
            <PulseLoader
              size={15}
              color='orange'
              loading={loading}
            />
          </Box>

          <ResponsiveContext.Consumer>
            {responsive => (
              <DataTable
                columns={responsive === 'small' ? userColumns.filter(el => (el.property !== 'email' && el.property !== 'created')) : userColumns}
                data={userValues}
                primaryKey='id'
                sort={sort}
                step={2000}
                onSort={setSort}
              />)}
          </ResponsiveContext.Consumer>
          {!userValues.length && <Text>There are no users to display</Text>}
        </Box>
      </Box>
      <Box background='white' direction='column' gap='small' round='small' margin={{ top: 'medium', bottom: 'large' }}>

        <Box direction='row-responsive' gap='xlarge' justify='between' pad={{ horizontal: 'medium', top: 'medium' }}>
          <Box>
            <Text color='#000' size='large' weight='bold'>Students</Text>
          </Box>
          <Box direction='row' gap='xsmall'>
          </Box>
        </Box>

        <Box direction='column' gap='medium' pad={{ horizontal: 'medium', bottom: 'medium' }}>
          <Box align='center'>
            <PulseLoader
              size={15}
              color='orange'
              loading={loading}
            />
          </Box>

          <ResponsiveContext.Consumer>
            {responsive => (
              <DataTable
                columns={responsive === 'small' ? studentColumns.filter(el => (el.property !== 'reference' && el.property !== 'created')) : studentColumns}
                data={studentValues}
                primaryKey='id'
                sort={sort}
                step={2000}
                onSort={setSort}
              />)}
          </ResponsiveContext.Consumer>
          {!studentValues.length && <Text>There are no students to display</Text>}
        </Box>
      </Box>
    </Box>
  );
}

export default OrganisationEdit
